/**
 * A CSV a Slovak-locale spreadsheet can add up.
 *
 * Written for `/statistics` (FB-105), moved here when `/reports` turned out to have a **second**
 * hand-rolled writer feeding **six** export buttons — with the same defect and one more:
 *
 * ```js
 * // legacy reports.tsx
 * const csv = rows.map(r => r.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n")
 * ```
 *
 * It quotes **every** cell, numbers included, so a spreadsheet reads `"1234.56"` as text and the total
 * row of a financial report cannot be summed. It joins with `\n` where RFC 4180 says `\r\n`. And the
 * amounts reaching it come from `n.toFixed(2).replace(".", ",")`, which is right by accident — a dot
 * decimal would have been the third defect.
 *
 * The rules, once:
 *
 * - **semicolon** delimiter, because 124 of the 1 113 product names carry a comma
 * - **comma** decimals, because that is what `sk-SK` reads as a number
 * - RFC 4180 quoting: quote only a field that needs it, and double an embedded quote
 * - a **UTF-8 byte-order mark**, or Excel reads `Kukuricová` in the local code page
 * - `\r\n` line endings and a trailing one
 * - a media type that declares the delimiter, so a spreadsheet opens it without asking
 */

export const CSV_DELIMITER = ';'

/**
 * U+FEFF, written as an escape.
 *
 * A zero-width mark pasted into source is invisible to the next reader, and the lint rule that bans
 * irregular whitespace is right to say so.
 */
export const UTF8_BOM = '﻿'

/**
 * One field, quoted only if it needs to be.
 *
 * Quoting unconditionally is what makes a number arrive as text. Quoting nothing is what lets a product
 * named `Pizza "Špeciál"` end its own field early and shift every column after it on that row.
 *
 * **How often that matters, measured 2026-08-06: 35 of this franchise's 173 product names — one in
 * five — carry a character this has to quote.** The figure moved here from `fieldsNeedingQuotes`, a
 * helper that counted them and was deleted with the rest of §9.35's list: the count was evidence for
 * this rule rather than a thing any screen needed, and evidence belongs beside what it justifies.
 */
export function csvField(value: string): string {
  const needsQuoting = /["\r\n;,]/.test(value)
  return needsQuoting ? `"${value.replace(/"/g, '""')}"` : value
}

/** `141680` cents → `1416,80`. Unquoted, so the spreadsheet reads it as a number. */
export function csvAmount(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',')
}

/** A plain number — a count, a quantity — with a comma decimal and no quotes. */
export function csvNumber(value: number, decimals = 0): string {
  if (!Number.isFinite(value)) return '0'
  return value.toFixed(decimals).replace('.', ',')
}

/**
 * A whole file from a header and its rows.
 *
 * Cells are pre-formatted strings: the caller has already decided which are text (through `csvField`)
 * and which are numbers (through `csvAmount` / `csvNumber`), because that decision is the difference
 * between a file a spreadsheet totals and one it does not.
 */
export function csvDocument(header: string[], rows: string[][]): string {
  const lines = [header.map(csvField).join(CSV_DELIMITER), ...rows.map((row) => row.join(CSV_DELIMITER))]
  return `${UTF8_BOM}${lines.join('\r\n')}\r\n`
}

/** `text/csv` with the delimiter declared, per RFC 4180. */
export function csvMediaType(): string {
  return `text/csv;charset=utf-8;header=present;delimiter=${CSV_DELIMITER}`
}

